import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency } from "@/utils/common";
import { calculatePf } from "../../../shared-utils/pfCalculatorCore.mjs";

/**
 * Provident Fund (PF) Calculator Component for calculating maturity amounts.
 * @example
 * PFCalculator()
 * <Card>...</Card>
 * @param {none} {none} - This component does not accept any arguments.
 * @returns {JSX.Element} A JSX element representing the PF Calculator UI.
 * @description
 *   - Utilizes useState and useEffect hooks to handle state and side effects.
 *   - Calculates monthly and employer contributions based on input percentages and salary.
 *   - Dynamically updates calculations when input values change.
 *   - Contains interactive UI components such as sliders and input fields for user customization.
 */
const PFCalculator: React.FC = () => {
  const [basicSalary, setBasicSalary] = useState<number>(30000);
  const [employeeContribution, setEmployeeContribution] = useState<number>(12);
  const [employerContribution, setEmployerContribution] = useState<number>(12);
  const [interestRate, setInterestRate] = useState<number>(8.15);
  const [years, setYears] = useState<number>(20);
  const [monthlyContribution, setMonthlyContribution] = useState<number>(0);
  const [results, setResults] = useState({
    totalContribution: 0,
    totalInterest: 0,
    maturityAmount: 0,
  });

  useEffect(() => {
    // Calculate monthly contribution
    const monthly = (basicSalary * employeeContribution) / 100;
    setMonthlyContribution(monthly);

    // Calculate employer contribution
    const employer = (basicSalary * employerContribution) / 100;

    const calculatedResults = calculatePf(monthly, employer, interestRate, years);
    setResults(calculatedResults);
  }, [basicSalary, employeeContribution, employerContribution, interestRate, years]);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>PF Calculator</CardTitle>
        <CardDescription>
          Calculate your Provident Fund maturity amount based on your contributions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic">Basic Parameters</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6">
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label htmlFor="basic-salary">Basic Salary (₹ per month)</Label>
                <div className="font-medium">{formatCurrency(basicSalary)}</div>
              </div>
              <Slider
                id="basic-salary"
                min={10000}
                max={200000}
                step={1000}
                value={[basicSalary]}
                onValueChange={(value) => setBasicSalary(value[0])}
              />
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  value={basicSalary}
                  onChange={(e) => setBasicSalary(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <Label htmlFor="years">Time Period (Years)</Label>
                <div className="font-medium">{years} years</div>
              </div>
              <Slider
                id="years"
                min={1}
                max={35}
                step={1}
                value={[years]}
                onValueChange={(value) => setYears(value[0])}
              />
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="advanced" className="space-y-6">
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label htmlFor="employee-contribution">Your Contribution (%)</Label>
                <div className="font-medium">{employeeContribution}%</div>
              </div>
              <Slider
                id="employee-contribution"
                min={0}
                max={30}
                step={0.5}
                value={[employeeContribution]}
                onValueChange={(value) => setEmployeeContribution(value[0])}
              />
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  value={employeeContribution}
                  onChange={(e) => setEmployeeContribution(Number(e.target.value))}
                  className="w-full"
                  step="0.5"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <Label htmlFor="employer-contribution">Employer Contribution (%)</Label>
                <div className="font-medium">{employerContribution}%</div>
              </div>
              <Slider
                id="employer-contribution"
                min={0}
                max={30}
                step={0.5}
                value={[employerContribution]}
                onValueChange={(value) => setEmployerContribution(value[0])}
              />
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  value={employerContribution}
                  onChange={(e) => setEmployerContribution(Number(e.target.value))}
                  className="w-full"
                  step="0.5"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <Label htmlFor="interest-rate">Interest Rate (%)</Label>
                <div className="font-medium">{interestRate}%</div>
              </div>
              <Slider
                id="interest-rate"
                min={0}
                max={12}
                step={0.01}
                value={[interestRate]}
                onValueChange={(value) => setInterestRate(value[0])}
              />
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="w-full"
                  step="0.01"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="p-4 bg-orange-50 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">Monthly Contribution</div>
          <div className="text-xl font-bold text-orange-700">
            {formatCurrency(monthlyContribution)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {employeeContribution}% of your Basic Salary
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <div className="text-sm text-gray-500 mb-1">Total Contribution</div>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(results.totalContribution)}
              </div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <div className="text-sm text-gray-500 mb-1">Total Interest</div>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(results.totalInterest)}
              </div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <div className="text-sm text-gray-500 mb-1">Maturity Amount</div>
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(results.maturityAmount)}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between border-t border-gray-200 pt-4">
        <Button
          variant="outline"
          onClick={() => {
            setBasicSalary(30000);
            setEmployeeContribution(12);
            setEmployerContribution(12);
            setInterestRate(8.15);
            setYears(20);
          }}
        >
          Reset
        </Button>
        <Button variant="default">Calculate</Button>
      </CardFooter>
    </Card>
  );
};

export default PFCalculator;
